import {
  DIAGNOSTIC_DEFINITION_KIND,
  EXAM_DEFINITION_KIND,
  PROTOCOL_DEFINITION_KIND,
  TREATMENT_DEFINITION_KIND,
} from "@repo/api";
import { quantityText } from "@repo/domain";
import type { Collection, InitialQueryBuilder } from "@tanstack/db";
import { and, eq, ilike, max, or, toArray, useLiveQuery } from "@tanstack/react-db";
import { useCollection } from "../core/hooks";
import type { FhirCollectionUtils } from "../fhir/base";
import type { ActivityDefinitionRow, DeviceDefinitionRow, PlanDefinitionRow } from "../fhir/types";
import { displayAnsweredValue } from "../lib/answered-value";
import { projectDeviceDefinitionRow } from "./device-definition-projection";
import { projectDiagnosticDefinitionRow } from "./diagnostic-definition-projection";
import { projectExamDefinition } from "./exam-definition";
import type { ProtocolPlannedTrialDto } from "./protocol-definition";
import { projectProtocolDefinitionRow } from "./protocol-definition-projection";
import { type EffectiveParameter, effectiveParameters, parameterAnswerKey } from "./shared/effective-parameters";
import type { TreatmentValueDefinitionDto } from "./treatment-definition";
import { projectTreatmentDefinitionRow } from "./treatment-definition-projection";

export interface ProtocolDefinitionDetailDto {
  definitionUrl: string;
  name: string;
  description?: string;
  availability: "Active" | "Inactive";
  version: number;
  authoredAt: string;
  totalDurationMinutes: number;
  envelopeMinutes: number;
  steps: Array<{
    id: string;
    costMinutes: number;
    startMinutes: number;
    procedures: Array<{
      id: string;
      title: string;
      typeLabel: "Diagnostic" | "Treatment" | null;
      requiredByExams: string[];
      hasResources: boolean;
      description?: string;
      paramBadges: Array<{
        id: string;
        source: string;
        sourceLabel?: string;
        label: string;
        display: string;
        unit?: string;
      }>;
      treatmentDefinitionUrl?: string;
      capturedPerTrial?: Array<{ id: string; label: string }>;
      /**
       * Absent when the occurrence plans none. Its declarations head the trial
       * columns; an occurrence with planned trials answers no parameters itself.
       */
      plannedTrials?: {
        parameters: Array<EffectiveParameter<ParameterDefinition>>;
        trials: readonly ProtocolPlannedTrialDto[];
      };
    }>;
  }>;
  exams: Array<{
    id: string;
    title: string;
    description?: string;
    declares: Array<{ name: string; stepLabel: string }>;
  }>;
}

export interface ProtocolDefinitionDetailFilters {
  availability?: ProtocolDefinitionDetailDto["availability"];
  search?: string;
}

interface ComposedProtocolRow extends PlanDefinitionRow {
  activityDefinitions: ActivityDefinitionRow[];
  deviceDefinitions: DeviceDefinitionRow[];
  examDefinitions: ActivityDefinitionRow[];
}

type PlanDefinitionCollection = Collection<PlanDefinitionRow, string, FhirCollectionUtils<PlanDefinitionRow>>;
type ActivityDefinitionCollection = Collection<
  ActivityDefinitionRow,
  string,
  FhirCollectionUtils<ActivityDefinitionRow>
>;
type DeviceDefinitionCollection = Collection<DeviceDefinitionRow, string, FhirCollectionUtils<DeviceDefinitionRow>>;
type ExamDefinitionCollection = Collection<ActivityDefinitionRow, string, FhirCollectionUtils<ActivityDefinitionRow>>;
type ComposedProtocolCollection = Collection<ComposedProtocolRow, string | number, Record<string, never>>;

type CurrentActivityDefinitionCollection = Collection<ActivityDefinitionRow, string | number, Record<string, never>>;
type CurrentDeviceDefinitionCollection = Collection<DeviceDefinitionRow, string | number, Record<string, never>>;
type CurrentExamDefinitionCollection = Collection<ActivityDefinitionRow, string | number, Record<string, never>>;

type ParameterDefinition = TreatmentValueDefinitionDto;

interface ResolvedProcedure {
  canonical: string;
  title: string;
  description?: string;
  kind: "Diagnostic" | "Treatment";
  hasResources: boolean;
  parameters: EffectiveParameter<ParameterDefinition>[];
  observations?: readonly { id: string; label: string }[];
}

function procedureTitle(name: string | undefined, qualifier?: string): string {
  const title = name || "Unavailable procedure";
  return qualifier ? `${title} · ${qualifier}` : title;
}

function parameterBadges(
  procedure: ResolvedProcedure,
  answers: readonly { source: string; parameterId: string; value: unknown }[],
) {
  const answersByKey = new Map(
    answers.map((answer) => [parameterAnswerKey(answer.source, answer.parameterId), answer]),
  );
  return procedure.parameters.map((parameter) => {
    const id = parameterAnswerKey(parameter.source, parameter.parameterId);
    const answer = answersByKey.get(id);
    const value = answer?.value as Parameters<typeof displayAnsweredValue>[1] | undefined;
    return {
      id,
      source: parameter.source,
      ...(parameter.sourceLabel ? { sourceLabel: parameter.sourceLabel } : {}),
      label: parameter.label,
      ...badgeValue(parameter.value, value),
    };
  });
}

/** A quantity's unit sits beside its number rather than inside the text. */
function badgeValue(
  definition: ParameterDefinition,
  value: Parameters<typeof displayAnsweredValue>[1] | undefined,
): { display: string; unit?: string } {
  if (value?.kind !== "Quantity") return { display: value ? displayAnsweredValue(definition, value) : "" };
  const text = quantityText(definition, value);
  return { display: text.format(value.value), ...(text.suffix === undefined ? {} : { unit: text.suffix }) };
}

export function projectProtocolDefinitionDetail(row: ComposedProtocolRow): ProtocolDefinitionDetailDto {
  const definition = projectProtocolDefinitionRow(row);
  const devices = row.deviceDefinitions.map(projectDeviceDefinitionRow);
  const devicesByUrl = new Map(devices.map((device) => [device.definitionUrl, device]));
  const procedures: ResolvedProcedure[] = row.activityDefinitions.map((resource) => {
    if (resource.catalogModelKind === DIAGNOSTIC_DEFINITION_KIND) {
      const diagnostic = projectDiagnosticDefinitionRow(resource);
      return {
        canonical: diagnostic.definitionUrl,
        title: diagnostic.name,
        ...(diagnostic.description ? { description: diagnostic.description } : {}),
        kind: "Diagnostic",
        observations: diagnostic.chartedObservations,
        hasResources: diagnostic.requirements.devices.length > 0 || diagnostic.requirements.locationType !== undefined,
        parameters: effectiveParameters<ParameterDefinition>(
          diagnostic.effectiveParameters,
          diagnostic.requirements.devices,
          devicesByUrl,
        ),
      };
    }
    const treatment = projectTreatmentDefinitionRow(resource);
    return {
      canonical: treatment.url,
      title: treatment.name,
      ...(treatment.description ? { description: treatment.description } : {}),
      kind: "Treatment",
      hasResources: treatment.deviceRequirements.length > 0 || treatment.locationRequirement !== undefined,
      parameters: effectiveParameters<ParameterDefinition>(
        treatment.effectiveParameters,
        treatment.deviceRequirements.map((requirement) => ({
          id: requirement.id,
          definitionUrl: requirement.definition,
        })),
        devicesByUrl,
      ),
    };
  });
  const proceduresByUrl = new Map(procedures.map((procedure) => [procedure.canonical, procedure]));
  const examsByUrl = new Map(
    row.examDefinitions.map((resource) => {
      const exam = projectExamDefinition(resource);
      return [exam.definitionUrl, exam] as const;
    }),
  );
  const steps = definition.steps.map((step, stepIndex) => {
    const costMinutes = step.placement.durationMinutes;
    return {
      id: `step-${stepIndex}`,
      costMinutes,
      startMinutes: step.placement.offsetMinutes,
      procedures: step.occurrences.map((occurrence) => {
        const procedure = proceduresByUrl.get(occurrence.definitionUrl);
        return {
          id: occurrence.occurrenceId,
          title: procedureTitle(procedure?.title, occurrence.qualifier),
          typeLabel: procedure?.kind ?? null,
          requiredByExams: [
            ...new Set(
              (occurrence.justifications ?? []).flatMap((reason) =>
                reason.kind === "exam" ? [examsByUrl.get(reason.exam)?.name ?? "Unavailable exam"] : [],
              ),
            ),
          ],
          hasResources: procedure?.hasResources ?? false,
          ...(procedure?.description ? { description: procedure.description } : {}),
          ...(occurrence.observationLevels?.length
            ? {
                capturedPerTrial: occurrence.observationLevels.map(({ observationId }) => ({
                  id: observationId,
                  label:
                    procedure?.observations?.find((observation) => observation.id === observationId)?.label ??
                    `Missing observation · ${observationId}`,
                })),
              }
            : {}),
          paramBadges: procedure ? parameterBadges(procedure, occurrence.parameterValues) : [],
          ...(procedure?.kind === "Treatment" ? { treatmentDefinitionUrl: procedure.canonical } : {}),
          ...(occurrence.plannedTrials?.length
            ? {
                plannedTrials: {
                  parameters: procedure?.parameters ?? [],
                  trials: occurrence.plannedTrials,
                },
              }
            : {}),
        };
      }),
    };
  });
  return {
    definitionUrl: definition.definitionUrl,
    name: definition.name,
    ...(definition.description === undefined ? {} : { description: definition.description }),
    availability: definition.availability,
    version: definition.version,
    authoredAt: definition.mintedAt,
    envelopeMinutes: definition.envelopeMinutes,
    totalDurationMinutes: Math.max(0, ...steps.map((step) => step.startMinutes + step.costMinutes)),
    steps,
    exams: definition.examDefinitionUrls.map((url) => {
      const exam = examsByUrl.get(url);
      return {
        id: url,
        title: exam?.name ?? "Unavailable exam",
        ...(exam?.description ? { description: exam.description } : {}),
        declares: [],
      };
    }),
  };
}

export function buildCurrentActivityDefinitionsQuery(
  query: InitialQueryBuilder,
  activities: ActivityDefinitionCollection,
) {
  const latestActivities = query
    .from({ candidate: activities })
    .where(({ candidate }) =>
      or(
        eq(candidate.catalogModelKind, DIAGNOSTIC_DEFINITION_KIND),
        eq(candidate.catalogModelKind, TREATMENT_DEFINITION_KIND),
      ),
    )
    .groupBy(({ candidate }) => candidate.url)
    .select(({ candidate }) => ({
      url: candidate.url,
      version: max(candidate.catalogVersion),
    }));
  return query
    .from({ activity: activities })
    .join({ latest: latestActivities }, ({ activity, latest }) => eq(activity.url, latest.url))
    .where(({ activity, latest }) =>
      and(
        or(
          eq(activity.catalogModelKind, DIAGNOSTIC_DEFINITION_KIND),
          eq(activity.catalogModelKind, TREATMENT_DEFINITION_KIND),
        ),
        eq(activity.catalogVersion, latest.version),
      ),
    )
    .select(({ activity }) => ({ ...activity }));
}

export function buildCurrentDeviceDefinitionsQuery(query: InitialQueryBuilder, devices: DeviceDefinitionCollection) {
  const latestDevices = query
    .from({ candidate: devices })
    .groupBy(({ candidate }) => candidate.deviceDefinitionUrl)
    .select(({ candidate }) => ({
      url: candidate.deviceDefinitionUrl,
      version: max(candidate.deviceDefinitionVersion),
    }));
  return query
    .from({ device: devices })
    .join({ latest: latestDevices }, ({ device, latest }) => eq(device.deviceDefinitionUrl, latest.url))
    .where(({ device, latest }) => eq(device.deviceDefinitionVersion, latest.version))
    .select(({ device }) => ({ ...device }));
}

export function buildCurrentExamDefinitionsQuery(query: InitialQueryBuilder, questionnaires: ExamDefinitionCollection) {
  const latestExams = query
    .from({ candidate: questionnaires })
    .where(({ candidate }) => eq(candidate.catalogModelKind, EXAM_DEFINITION_KIND))
    .groupBy(({ candidate }) => candidate.url)
    .select(({ candidate }) => ({
      lineage: candidate.url,
      version: max(candidate.catalogVersion),
    }));
  return query
    .from({ exam: questionnaires })
    .join({ latest: latestExams }, ({ exam, latest }) => eq(exam.url, latest.lineage))
    .where(({ exam, latest }) =>
      and(eq(exam.catalogModelKind, EXAM_DEFINITION_KIND), eq(exam.catalogVersion, latest.version)),
    )
    .select(({ exam }) => ({ ...exam }));
}

export function buildProtocolDefinitionDetailRowsQuery(
  query: InitialQueryBuilder,
  plans: PlanDefinitionCollection,
  currentActivities: CurrentActivityDefinitionCollection,
  currentDevices: CurrentDeviceDefinitionCollection,
  currentExams: CurrentExamDefinitionCollection,
  filters: ProtocolDefinitionDetailFilters,
) {
  const status =
    filters.availability === undefined ? undefined : filters.availability === "Active" ? "active" : "retired";
  const search = filters.search?.trim();
  const latestProtocols = query
    .from({ candidate: plans })
    .where(({ candidate }) => eq(candidate.catalogModelKind, PROTOCOL_DEFINITION_KIND))
    .groupBy(({ candidate }) => candidate.url)
    .select(({ candidate }) => ({ url: candidate.url, version: max(candidate.catalogVersion) }));
  let protocols = query
    .from({ definition: plans })
    .join({ latest: latestProtocols }, ({ definition, latest }) => eq(definition.url, latest.url))
    .where(({ definition, latest }) =>
      and(eq(definition.catalogModelKind, PROTOCOL_DEFINITION_KIND), eq(definition.catalogVersion, latest.version)),
    );
  if (status) protocols = protocols.where(({ definition }) => eq(definition.status, status));
  if (search) {
    protocols = protocols.where(({ definition }) =>
      or(ilike(definition.title, `%${search}%`), ilike(definition.description, `%${search}%`)),
    );
  }

  return protocols
    .orderBy(({ definition }) => definition.title, "asc")
    .select(({ definition }) => ({
      ...definition,
      activityDefinitions: toArray(
        query
          .from({ activity: currentActivities })
          .where(({ activity }) => eq(activity.catalogViewScope, definition.catalogViewScope))
          .select(({ activity }) => ({ ...activity })),
      ),
      deviceDefinitions: toArray(
        query
          .from({ device: currentDevices })
          .where(({ device }) => eq(device.catalogViewScope, definition.catalogViewScope))
          .select(({ device }) => ({ ...device })),
      ),
      examDefinitions: toArray(
        query
          .from({ exam: currentExams })
          .where(({ exam }) => eq(exam.catalogViewScope, definition.catalogViewScope))
          .select(({ exam }) => ({ ...exam })),
      ),
    }));
}

export function buildProtocolDefinitionDetailRowQuery(
  query: InitialQueryBuilder,
  plans: PlanDefinitionCollection,
  currentActivities: CurrentActivityDefinitionCollection,
  currentDevices: CurrentDeviceDefinitionCollection,
  currentExams: CurrentExamDefinitionCollection,
  definitionUrl: string | undefined,
) {
  if (!definitionUrl) return undefined;
  return buildProtocolDefinitionDetailRowsQuery(query, plans, currentActivities, currentDevices, currentExams, {})
    .where(({ definition }) => eq(definition.url, definitionUrl))
    .findOne();
}

export function buildProtocolDefinitionDetailDtosQuery(
  query: InitialQueryBuilder,
  definitions: ComposedProtocolCollection,
) {
  return query
    .from({ definition: definitions })
    .fn.select(({ definition }) => projectProtocolDefinitionDetail(definition));
}

export function buildProtocolDefinitionDetailDtoQuery(
  query: InitialQueryBuilder,
  definitions: ComposedProtocolCollection | undefined,
) {
  if (!definitions) return undefined;
  return buildProtocolDefinitionDetailDtosQuery(query, definitions).findOne();
}

function useCurrentCatalogDefinitions() {
  const activities = useCollection("activityDefinition");
  const devices = useCollection("deviceDefinition");
  const questionnaires = useCollection("activityDefinition");
  const currentActivities = useLiveQuery({
    query: (query) => buildCurrentActivityDefinitionsQuery(query, activities),
  });
  const currentDevices = useLiveQuery({
    query: (query) => buildCurrentDeviceDefinitionsQuery(query, devices),
  });
  const currentExams = useLiveQuery({
    query: (query) => buildCurrentExamDefinitionsQuery(query, questionnaires),
  });
  return {
    currentActivities: currentActivities.collection as unknown as CurrentActivityDefinitionCollection,
    currentDevices: currentDevices.collection as unknown as CurrentDeviceDefinitionCollection,
    currentExams: currentExams.collection as unknown as CurrentExamDefinitionCollection,
  };
}

export function useProtocolDefinitionDetails(
  filters: ProtocolDefinitionDetailFilters = {},
): ProtocolDefinitionDetailDto[] {
  const plans = useCollection("planDefinition");
  const { currentActivities, currentDevices, currentExams } = useCurrentCatalogDefinitions();
  const composed = useLiveQuery({
    queryKey: [
      currentActivities.id,
      currentDevices.id,
      currentExams.id,
      "protocol-definition-details-rows",
      filters.availability ?? null,
      filters.search ?? null,
    ],
    query: (query) =>
      buildProtocolDefinitionDetailRowsQuery(query, plans, currentActivities, currentDevices, currentExams, filters),
  });
  const result = useLiveQuery({
    queryKey: [composed.collection.id, "protocol-definition-details-projection"],
    query: (query) =>
      buildProtocolDefinitionDetailDtosQuery(query, composed.collection as unknown as ComposedProtocolCollection),
  });
  return result.data as ProtocolDefinitionDetailDto[];
}

export function useProtocolDefinitionDetail(
  definitionUrl: string | undefined,
): ProtocolDefinitionDetailDto | undefined {
  const plans = useCollection("planDefinition");
  const { currentActivities, currentDevices, currentExams } = useCurrentCatalogDefinitions();
  const composed = useLiveQuery({
    queryKey: [
      currentActivities.id,
      currentDevices.id,
      currentExams.id,
      "protocol-definition-detail-rows",
      definitionUrl ?? null,
    ],
    query: (query) =>
      buildProtocolDefinitionDetailRowQuery(
        query,
        plans,
        currentActivities,
        currentDevices,
        currentExams,
        definitionUrl,
      ),
  });
  const result = useLiveQuery({
    queryKey: [composed.collection?.id ?? null, "protocol-definition-detail-projection", definitionUrl ?? null],
    query: (query) => {
      if (!definitionUrl) return undefined;
      return buildProtocolDefinitionDetailDtoQuery(
        query,
        composed.collection as unknown as ComposedProtocolCollection | undefined,
      );
    },
  });
  return result.data as ProtocolDefinitionDetailDto | undefined;
}
